/**
 * `entities/project-folder` API surface.
 *
 * Exposes:
 *   - Service functions for READING folders + folder files.
 *   - `*Query` queryOptions helpers for TanStack consumers — reads MUST flow
 *     through queryOptions to enforce the cache-key contract.
 *
 * Mutations (`createProjectFolder`, `updateProjectFolder`, `deleteProjectFolder`)
 * live in `features/manage-project-folders/`: entities expose queries,
 * features own mutations.
 *
 * Migrated from `features/projects/api/projectFolders.api.ts`.
 */
import {
  ProjectFilesService,
  ProjectFoldersService,
  type ProjectFileResponseDto,
} from '@inference.engine/workspace-api-client/web';
import { queryOptions } from '@tanstack/react-query';

import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

import { PROJECT_FOLDER_QUERY_KEYS } from './query-keys';
import type {
  ProjectFolder,
  ProjectFoldersListQuery,
  ProjectFoldersResponse,
} from '../model/types';

// ──────────────────────────── Service functions ────────────────────────────

export const listProjectFolders = async (
  platformProjectId: string,
  params?: ProjectFoldersListQuery,
): Promise<ProjectFoldersResponse> => {
  const response = await ProjectFoldersService.listFolders({
    client: workspaceHttp,
    path: { platformProjectId },
    query: params,
  });

  if (!response.data) {
    throw new Error('Failed to list project folders');
  }

  return response.data;
};

export const getProjectFolder = async (
  platformProjectId: string,
  folderId: string,
): Promise<ProjectFolder> => {
  const response = await ProjectFoldersService.getFolderById({
    client: workspaceHttp,
    path: { platformProjectId, folderId },
    // FolderDetailPage owns error UI for this query (a "folder not found"
    // card); suppress the global interceptor toast so the user doesn't see
    // two stacked errors for the same failed fetch.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to get project folder');
  }

  return response.data;
};

// ──────────────────────────── queryOptions helpers ─────────────────────────

interface ProjectFoldersQueryOptions {
  parentId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Paginated/searched folder list. The cache key shape is identical to the
 * historical `PROJECTS_QUERY_KEYS.folders.list(...)` plus a 6th element
 * carrying the search/page/pageSize triple, so an existing query cache
 * survives the relocation. Pinned by `__tests__/cache-contract.test.ts`.
 */
export const projectFoldersQuery = (
  platformProjectId: string | undefined,
  options: ProjectFoldersQueryOptions = {},
) => {
  const { parentId, search, page, pageSize } = options;
  return queryOptions({
    queryKey: [
      ...PROJECT_FOLDER_QUERY_KEYS.list(platformProjectId!, parentId),
      { search, page, pageSize },
    ],
    queryFn: () =>
      listProjectFolders(platformProjectId!, {
        parentId: parentId ?? '',
        ...(search ? { search } : {}),
        ...(page !== undefined ? { page } : {}),
        ...(pageSize !== undefined ? { pageSize } : {}),
      }),
    enabled: !!platformProjectId,
  });
};

export const projectFolderQuery = (
  platformProjectId: string | undefined,
  folderId: string | undefined,
) =>
  queryOptions({
    queryKey: PROJECT_FOLDER_QUERY_KEYS.detail(platformProjectId!, folderId!),
    queryFn: () => getProjectFolder(platformProjectId!, folderId!),
    enabled: !!platformProjectId && !!folderId,
  });

type FilesSortBy = 'file_name' | 'created_at' | 'latest_version_created_at';
type FilesSortOrder = 'ASC' | 'DESC';

interface ProjectFolderFilesQueryOptions {
  status?: ProjectFileResponseDto['status'];
  name?: string;
  sortBy?: FilesSortBy;
  sortOrder?: FilesSortOrder;
}

export const projectFolderFilesQuery = (
  platformProjectId: string | undefined,
  folderId: string | undefined,
  options: ProjectFolderFilesQueryOptions = {},
) => {
  const { status, name, sortBy, sortOrder } = options;
  const hasFilter = Boolean(status) || Boolean(name) || Boolean(sortBy) || Boolean(sortOrder);
  return queryOptions({
    // Append the filter slot only when at least one filter is set so the
    // no-filter cache key keeps the historical 5-tuple shape pinned by the
    // cache-contract test. The exhaustive-deps rule can't trace identifiers
    // through the ternary, but all option fields are covered across the two
    // branches.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: hasFilter
      ? ([
          ...PROJECT_FOLDER_QUERY_KEYS.files(platformProjectId!, folderId!),
          { status, name, sortBy, sortOrder },
        ] as const)
      : PROJECT_FOLDER_QUERY_KEYS.files(platformProjectId!, folderId!),
    queryFn: () =>
      ProjectFilesService.listFiles({
        client: workspaceHttp,
        path: { platformProjectId: platformProjectId! },
        query: {
          folderId: [folderId!],
          ...(status ? { status: [status] } : {}),
          ...(name ? { name } : {}),
          ...(sortBy ? { sortBy } : {}),
          ...(sortOrder ? { sortOrder } : {}),
        },
      }).then((response) => {
        if (!response.data) {
          throw new Error('Failed to list folder files');
        }
        return response.data;
      }),
    enabled: !!platformProjectId && !!folderId,
  });
};
